/**
 * Floating score labels.
 *
 * Screen-space and fire-and-forget: a label is projected once, where the
 * thing that earned it happened, then rises and fades on its own. It is not
 * re-projected per frame, because a label that tracked an orbiting camera for
 * a second would read as a HUD element rather than a reaction.
 */

export interface ScorePopup {
  readonly key: number;
  readonly x: number;
  readonly y: number;
  readonly text: string;
  readonly color: string;
  /** Wall clock at which it was raised, in milliseconds. */
  readonly bornAt: number;
}

/** More than this on screen is a pile-up, not feedback. */
const MAX_POPUPS = 12;

export function addPopup(
  popups: ScorePopup[],
  popup: ScorePopup,
): ScorePopup[] {
  const next = [...popups, popup];
  return next.length > MAX_POPUPS ? next.slice(next.length - MAX_POPUPS) : next;
}

/**
 * Drop the labels whose animation has finished.
 *
 * Returns the same array when nothing expired, so the HUD tick does not
 * churn state ten times a second for no reason.
 */
export function prunePopups(
  popups: ScorePopup[],
  now: number,
  lifetimeMs: number,
): ScorePopup[] {
  const live = popups.filter((popup) => now - popup.bornAt < lifetimeMs);
  return live.length === popups.length ? popups : live;
}
